import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { usePosts } from "../../../hooks/useCommunity";
import communityAPI from "../../../api/community";
import Avatar from "../../../componentes/ui/Avatar";
import verifiedBadge from "../../../assets/verified.png";
import Badge from "../../../componentes/ui/Badge";
import { PostActions } from "../shared/CRUDActions";
import { PostEditModal } from "../shared/EditModal";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";
import CreatePost from "../shared/CreatePost";
import { FullscreenSpinner } from '../../../componentes/LoadingIndicator';
import { formatDate } from "../../../utils/dateFormat";
import { formatNumber } from "../../../utils/numberFormat";
import { Loader2, Plus, Heart, MessageCircle, Eye } from "lucide-react";
import { truncateText } from "../../../utils/truncateText";

const normalizeText = (value) => (value ? String(value).toLowerCase() : "");

const getCommentsCount = (thread) =>
  thread?.comments_count ?? thread?.comments_total ?? 0;

const isThreadSolved = (thread) =>
  typeof thread?.is_solved === "boolean"
    ? thread.is_solved
    : thread?.status === "solved";

const matchesCategory = (thread, value) => {
  if (!thread?.category || !value || value === "all") {
    return false;
  }

  const normalized = normalizeText(value);
  const category = thread.category;
  const slug = category.slug ? normalizeText(category.slug) : null;
  const idValue =
    category.id !== undefined ? normalizeText(String(category.id)) : null;
  const name = category.name ? normalizeText(category.name) : null;

  return slug === normalized || idValue === normalized || name === normalized;
};

const threadMatchesSearch = (thread, searchValue) => {
  const normalized = normalizeText(searchValue);
  if (!normalized) {
    return true;
  }

  const fields = [
    thread?.title,
    thread?.excerpt,
    thread?.content,
    thread?.category?.name,
    thread?.author?.name,
    thread?.author?.username,
  ];

  return fields.some((field) => normalizeText(field).includes(normalized));
};

const getThreadKey = (thread) =>
  thread?.slug ?? thread?.id ?? thread?.uuid ?? thread?.pk ?? null;

export default function ForumList({ isOwn = false }) {
  const { user } = useCommunityAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Latest");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setDebouncedSearch("");
  }, []);

  const sentinelRef = useRef(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const {
    data: categoryStats = [],
    isLoading: categoriesLoading,
  } = useQuery({
    queryKey: ["community", "categories", "forum", "withCounts"],
    queryFn: async () => {
      const res = await communityAPI.categories.getWithCounts({
        post_type: "forum",
      });
      const payload = res.data;
      return Array.isArray(payload) ? payload : payload?.results || [];
    },
    staleTime: 60_000,
  });

  const {
    data: pinnedThreads = [],
    isLoading: pinnedLoading,
  } = useQuery({
    queryKey: ["community", "posts", "forum", "pinned", { isOwn }],
    queryFn: async () => {
      const params = {
        post_type: "forum",
        is_pinned: true,
      };
      if (isOwn) {
        params.mine = true;
      } else {
        params.is_approved = true;
      }
      const res = await communityAPI.posts.getAll(params);
      const payload = res.data;
      return Array.isArray(payload) ? payload : payload?.results || [];
    },
    staleTime: 30_000,
  });

  const filters = useMemo(() => {
    const params = {
      post_type: "forum",
      ordering: "-created_at",
    };

    if (isOwn) {
      params.mine = true;
    } else {
      // Public forum listing should not show unapproved threads (even to the author).
      params.is_approved = true;
    }

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    if (selectedCategory !== "all") {
      params.category_slug = selectedCategory;
    }

    if (statusFilter === "Solved") {
      params.is_solved = true;
    } else if (statusFilter === "Unsolved") {
      params.is_solved = false;
    }

    if (sortBy === "Top") {
      params.ordering = "-likes_total";
    } else if (sortBy === "Latest") {
      params.ordering = "-created_at";
    }

    return params;
  }, [isOwn, debouncedSearch, selectedCategory, statusFilter, sortBy]);

  const {
    posts,
    loading: postsLoading,
    loadingMore,
    totalCount,
    hasNext,
    loadMore,
    refetch,
    setPage,
  } = usePosts(filters, {
    paginated: true,
    pageSize: 10,
    infinite: true,
    dedupeKey: (item) => item?.slug ?? item?.id,
    resetOnFilters: true,
  });

  useEffect(() => {
    if (!hasInitialized && !postsLoading && !categoriesLoading && !pinnedLoading) {
      setHasInitialized(true);
    }
  }, [hasInitialized, postsLoading, categoriesLoading, pinnedLoading]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCategory, statusFilter, sortBy, setPage]);

  const fallbackCategories = useMemo(() => {
    const map = new Map();
    [...(posts || []), ...(pinnedThreads || [])].forEach((thread) => {
      const category = thread?.category;
      if (!category) {
        return;
      }
      const value =
        category.slug ||
        (category.id !== undefined ? String(category.id) : category.name);
      if (!value || map.has(value)) {
        return;
      }
      map.set(value, {
        label: category.name || "General",
        value,
        count: null,
      });
    });
    return Array.from(map.values());
  }, [posts, pinnedThreads]);

  const categoryOptions = useMemo(() => {
    const options = [
      { label: "All Categories", value: "all", count: totalCount },
    ];

    const seen = new Set(options.map((option) => option.value));

    (categoryStats || []).forEach((cat) => {
      const value =
        cat.slug || (cat.id !== undefined ? String(cat.id) : cat.name);
      if (!value || seen.has(value)) {
        return;
      }
      options.push({
        label: cat.name,
        value,
        count: cat.post_count ?? 0,
      });
      seen.add(value);
    });

    fallbackCategories.forEach((option) => {
      if (!seen.has(option.value)) {
        options.push(option);
        seen.add(option.value);
      }
    });

    return options;
  }, [categoryStats, fallbackCategories, totalCount]);

  const categoryOptionMap = useMemo(() => {
    const map = new Map();
    categoryOptions.forEach((option) => {
      map.set(option.value, option);
    });
    return map;
  }, [categoryOptions]);

  useEffect(() => {
    if (
      selectedCategory !== "all" &&
      !categoryOptionMap.has(selectedCategory)
    ) {
      setSelectedCategory("all");
    }
  }, [selectedCategory, categoryOptionMap]);

  const selectedCategoryLabel =
    categoryOptionMap.get(selectedCategory)?.label || "All Categories";

  const profileLinkState = useMemo(
    () => ({ fromCommunity: !isOwn, fromDashboard: isOwn }),
    [isOwn]
  );

  const filteredPinnedThreads = useMemo(() => {
    return (pinnedThreads || []).filter((thread) => {
      if (selectedCategory !== "all" && !matchesCategory(thread, selectedCategory)) {
        return false;
      }
      if (statusFilter === "Solved" && !isThreadSolved(thread)) {
        return false;
      }
      if (statusFilter === "Unsolved" && isThreadSolved(thread)) {
        return false;
      }
      if (sortBy === "Unanswered" && getCommentsCount(thread) > 0) {
        return false;
      }
      if (debouncedSearch && !threadMatchesSearch(thread, debouncedSearch)) {
        return false;
      }
      return true;
    });
  }, [
    pinnedThreads,
    selectedCategory,
    statusFilter,
    sortBy,
    debouncedSearch,
  ]);

  const visibleThreads = useMemo(() => {
    const pinnedKeys = new Set(
      filteredPinnedThreads
        .map((thread) => getThreadKey(thread))
        .filter(Boolean)
    );

    const base = (posts || []).filter((thread) => {
      const key = getThreadKey(thread);
      if (pinnedKeys.has(key) || thread.is_pinned) {
        return false;
      }

      if (selectedCategory !== "all" && !matchesCategory(thread, selectedCategory)) {
        return false;
      }

      if (statusFilter === "Solved" && !isThreadSolved(thread)) {
        return false;
      }

      if (statusFilter === "Unsolved" && isThreadSolved(thread)) {
        return false;
      }

      if (sortBy === "Unanswered" && getCommentsCount(thread) > 0) {
        return false;
      }

      if (debouncedSearch && !threadMatchesSearch(thread, debouncedSearch)) {
        return false;
      }

      return true;
    });

    if (sortBy === "Top") {
      return [...base].sort((a, b) => {
        const aLikes = a.likes_total ?? a.likes_count ?? 0;
        const bLikes = b.likes_total ?? b.likes_count ?? 0;
        return bLikes - aLikes;
      });
    }

    return [...base].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [
    posts,
    filteredPinnedThreads,
    selectedCategory,
    statusFilter,
    sortBy,
    debouncedSearch,
  ]);

  const handleLoadMore = useCallback(() => {
    if (!hasNext || postsLoading || loadingMore) {
      return;
    }
    loadMore().catch(() => {});
  }, [hasNext, postsLoading, loadingMore, loadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    if (!hasNext) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleLoadMore();
          }
        });
      },
      { root: null, rootMargin: "320px 0px 320px 0px", threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [handleLoadMore, hasNext]);

  const showInitialLoader =
    !hasInitialized && (postsLoading || categoriesLoading || pinnedLoading);
  const showThreadsLoading = hasInitialized && postsLoading;

  if (
    showInitialLoader &&
    visibleThreads.length === 0 &&
    filteredPinnedThreads.length === 0
  ) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 blog-body">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <FullscreenSpinner message="Loading threads..." tone="slate" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0E0E0E] pt-24 blog-body">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-950 dark:text-white leading-tight tracking-tight mb-4">
            {isOwn ? "My Forum Threads" : "Community Forum"}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {isOwn
              ? "Manage your discussions and questions."
              : "Connect, share knowledge, and get help from our vibrant community"}
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
          <div className="relative flex-1 w-full max-w-md">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search threads..."
              className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-[#161616] text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 shadow-lg"
            />
            <svg
              className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchTerm ? (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Clear search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ) : null}
          </div>
          <div className="flex flex-col items-stretch gap-4 w-full md:w-auto md:flex-wrap md:flex-row md:items-center md:justify-center">
            <div className="bg-white dark:bg-[#161616] p-1 rounded-xl shadow-lg">
              <div className="flex items-center gap-1">
                {["Latest", "Top", "Unanswered"].map((sort) => (
                  <button
                    key={sort}
                    onClick={() => setSortBy(sort)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      sortBy === sort
                        ? "bg-primary text-white shadow-md"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    {sort}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-[#161616] p-1 rounded-xl shadow-lg">
              <div className="flex items-center gap-1">
                {["All", "Solved", "Unsolved"].map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      statusFilter === status
                        ? "bg-green-600 text-white shadow-md"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full md:w-auto flex items-center gap-2 px-4 py-3 bg-white dark:bg-[#161616] rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                <span>{selectedCategoryLabel}</span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    showCategoryDropdown ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showCategoryDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-40 bg-black/5"
                    onClick={() => setShowCategoryDropdown(false)}
                  />

                  <div className="absolute top-full left-0 right-0 z-50 mt-2 bg-white dark:bg-[#161616] rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
                    <div className="max-h-64 overflow-y-auto py-1">
                      {categoryOptions.map((option) => {
                        const count =
                          option.value === "all"
                            ? totalCount
                            : categoryOptionMap.get(option.value)?.count;

                        if (
                          option.value !== "all" &&
                          typeof count === "number" &&
                          count === 0
                        ) {
                          return null;
                        }

                        return (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSelectedCategory(option.value);
                              setShowCategoryDropdown(false);
                              setPage(1);
                            }}
                            className={`w-full flex items-center justify-between px-4 py-3 transition-all duration-150 group ${
                              selectedCategory === option.value
                                ? "bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary/80 border-l-4 border-l-indigo-600"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            }`}
                          >
                            <span className="font-medium">{option.label}</span>
                            {typeof count === "number" ? (
                              <span className="ml-4 px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors duration-150">
                                {formatNumber(count)}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {user && (
            <div className="flex items-center w-full md:w-auto">
              <button
                onClick={() => setShowCreate(true)}
                className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary hover:bg-[#229bb8] text-white font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                <Plus className="w-4 h-4" />
                New Thread
              </button>
            </div>
          )}
        </div>

        {pinnedLoading ? (
          <div className="flex items-center justify-center py-6">
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              Loading pinned threads...
            </span>
          </div>
        ) : (
          filteredPinnedThreads.map((t) => (
            <div
              key={t.id}
              className="p-4 sm:p-6 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-2xl shadow-lg border border-amber-200 dark:border-amber-800 flex flex-col md:flex-row items-start md:items-center gap-4 mb-6"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-xl shrink-0">
                <svg
                  className="w-6 h-6 text-amber-600 dark:text-amber-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={
                        isOwn
                          ? `/community/forum/${t.slug}`
                          : `/forum/${t.slug}`
                      }
                      className="font-bold text-lg text-gray-900 dark:text-white mb-1 hover:text-primary transition-colors block sm:truncate"
                    >
                      {t.title}
                    </Link>
                    <div className="flex items-center gap-3 mt-1">
                      <Badge color="amber">Pinned</Badge>
                      <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                        <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {formatNumber(getCommentsCount(t))}</span>
                        <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {formatNumber(t.views || 0)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {user && user.id === t.author?.id && (
                      <div
                        className="shrink-0"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <PostActions
                          post={t}
                          onEdit={() => setEditingPost(t)}
                          onDeleted={() => {
                            refetch().catch(() => {});
                          }}
                        />
                      </div>
                    )}
                    <div className="hidden sm:block">
                      <Avatar
                        src={t.author?.avatar_url || t.author?.avatar}
                        name={[t.author?.first_name, t.author?.last_name].filter(Boolean).join(" ").trim() || t.author?.name || t.author?.username || "Anonymous"}
                        size={user && user.id === t.author?.id ? 10 : 10}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        {showThreadsLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div key={`thread-skeleton-${index}`} className="block">
              <div className="p-6 bg-white dark:bg-[#161616] rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 mb-6 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700" />
                  <div className="flex-1 space-y-4">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-4">
                      <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                      <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
                      <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : visibleThreads.length === 0 ? (
          <div className="text-center py-16">
            <svg
              className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No threads found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : (
          visibleThreads.map((t) => (
            <Link
              key={t.id}
              to={isOwn ? `/community/forum/${t.slug}` : `/forum/${t.slug}`}
              className="block group"
            >
              <div className="p-4 sm:p-6 bg-white dark:bg-[#161616] rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700 mb-6">
                <div className="flex flex-row items-start gap-4">
                  <div className="shrink-0 hidden sm:block sm:mt-2">
                    <Avatar
                      src={t.author?.avatar_url || t.author?.avatar}
                      name={[t.author?.first_name, t.author?.last_name].filter(Boolean).join(" ").trim() || t.author?.name || t.author?.username || "Anonymous"}
                      size={10}
                    />
                  </div>
                  <div className="flex-1 min-w-0 w-full">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-lg text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary/80 transition-colors duration-200 line-clamp-2">
                          {t.title}
                        </h3>
                        {!t.is_approved && (
                          <Badge
                            color="orange"
                            className="text-xs whitespace-nowrap"
                          >
                            Pending Approval
                          </Badge>
                        )}
                        {isThreadSolved(t) && (
                          <Badge color="green" className="flex items-center gap-1">
                            <svg
                              className="w-4 h-4 flex-shrink-0 text-green-600 dark:text-green-400"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="9"
                                fill="currentColor"
                                fillOpacity="0.15"
                              />
                              <path
                                d="M9 12l2 2 4-4"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Solved
                          </Badge>
                        )}
                      </div>
                      {user && user.id === t.author?.id && (
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <PostActions
                            post={t}
                            onEdit={() => setEditingPost(t)}
                            onDeleted={() => {
                              refetch().catch(() => {});
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="text-sm text-gray-600 dark:text-gray-400 overflow-hidden text-ellipsis whitespace-nowrap">
                        by{" "}
                        {t.author?.username ? (
                          <span
                            role="link"
                            tabIndex={0}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              window.location.href = `/profile/${t.author.username}`;
                            }}
                            className="font-medium hover:text-primary dark:hover:text-primary/80 transition-colors cursor-pointer inline-flex items-center gap-1.5"
                          >
                            {[t.author?.first_name, t.author?.last_name].filter(Boolean).join(" ").trim() || t.author.name || t.author.username}
                            {(t.author.is_trusted || t.author.status?.is_trusted) && (
                              <img
                                src={verifiedBadge}
                                alt="Verified"
                                className="w-3.5 h-3.5 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                              />
                            )}
                          </span>
                        ) : (
                          <span className="font-medium">Anonymous</span>
                        )}{" "}
                        • {formatDate(t.created_at)}
                      </div>
                      {t.category && (
                        <div className="flex items-center gap-2">
                          <Badge color="yellow" className="text-xs">
                            {t.category.name || t.category}
                          </Badge>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <Heart className="w-4 h-4" />
                            {formatNumber(t.likes_total ?? t.likes_count ?? 0)}
                          </div>
                          <div className="flex items-center gap-1">
                            <MessageCircle className="w-4 h-4" />
                            {formatNumber(getCommentsCount(t))}
                          </div>
                          <div className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {formatNumber(t.views || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}

        <div ref={sentinelRef} className="h-2 w-full" />
        <div className="pt-6 flex flex-col items-center gap-4">
          {loadingMore && (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading more threads...</span>
            </div>
          )}
          {!loadingMore && hasNext && (
            <button
              type="button"
              onClick={handleLoadMore}
              className="px-5 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#161616] text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm hover:shadow-md transition-all"
            >
              Load more threads
            </button>
          )}
          {!hasNext && posts.length > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              You&apos;re all caught up.
            </p>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-[#161616] rounded-2xl w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Create New Thread
              </h3>
              <button
                onClick={() => setShowCreate(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            <CreatePost type="forum" />
          </div>
        </div>
      )}

      <PostEditModal
        visible={!!editingPost}
        post={editingPost}
        onClose={() => setEditingPost(null)}
        onUpdated={() => {
          refetch().catch(() => {});
        }}
      />
    </div>
  );
}

ForumList.propTypes = {
  isOwn: PropTypes.bool,
};
